
import {
  Button,
  FormControlLabel,
  Grid,
  Switch,
  TextField,
} from "@mui/material";
import { BaseTextField } from "components/FormBuilder/fields/BaseTextField";
import AppForm from "components/Shared/AppForm";
import BaseAutocomplete from "components/Shared/Base/BaseAutocomplete";
import { CustomCountryPhoneInput } from "components/Shared/CustomCountryPhoneInput";
import { useAuth } from "hooks";
import React from "react";
import { Controller, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import BaseModal from "components/Shared/BaseModel/BaseModel";
import { toast } from "react-toastify";
import { useCreateMapAddressMutation } from "store/services/mapAddressApi";
import { useLazyGetDDLSystemUserListQuery } from "store/services/userApi";
import { useGetDDLAccountListQuery } from "store/services/accountApi";
import { useLazyGetDDLEntityListQuery } from "store/services/entityApi";
import {
  useLazyGetDDLBranchListQuery,
  useLazyGetBranchByRegionDDLQuery,
} from "store/services/branchApi";
import { useLazyGetDDLRestrictedRegionListQuery } from "store/services/LocationsApi/regionApi";
import BaseSelect from "components/Shared/Base/BaseSelect";
import { PAYMENT_TERMS_OPTIONS } from "data/static/staticOptions";
import NewAddressForm from "screens/App/CRM/MapAddress/Components/NewMapAddressForm";

const INITIAL_VALUE = {
  shopName: "",
  phone: "",
  email: "",
  website: "",
  address: "",
  ownerName: "",
  registrationNo: "",
  paymentTerms: "",
  relationshipManager: "",
  onboardedBy: "",
  referredBy: "",
  isActive: true,
  account: "",
  entity: "",
  branch: [],
  province: [],
  serviceBranches: [],
};

const VendorProfileForm = ({
  vendorProfileDetail = null,
  isBusy = false,
  onSubmit = () => { },
}) => {
  const navigate = useNavigate();
  const { isVendor, viewEntity, isAccountAdmin, isSystemAdmin } = useAuth();

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
    setValue,
  } = useForm({
    defaultValues: {
      ...INITIAL_VALUE,
      coordinates: vendorProfileDetail?.coordinates || { lat: "", long: "" },
      canAddress: vendorProfileDetail?.canAddress || "",
      relationshipManager: vendorProfileDetail?.relationshipManager,
      referredBy: vendorProfileDetail?.referredBy,
      onboardedBy: vendorProfileDetail?.onboardedBy,
      account: viewEntity?.entity?.account || "",
      entity: viewEntity?.entity,
      branch: [],
      province: [],
      serviceBranches: vendorProfileDetail?.serviceBranches || [],
    },
  });

  const [isAddAddressModalActive, setIsAddAddressModalActive] =
    React.useState(false);
  const [branchOptions, setBranchOptions] = React.useState([]);
  const [canAddress] = React.useState("");
  const [createAddress, { isLoading: creatingAddress }] =
    useCreateMapAddressMutation();

  const address = watch("address");
  const _watchAccount = watch("account");
  const _watchEntity = watch("entity");

  const [
    fetchVenUsers,
    {
      data: userVenList = [],
      isLoading: loadingVenUsers,
      isFetching: fetchingVenUsers,
    },
  ] = useLazyGetDDLSystemUserListQuery();

  const { data: accounts, isLoading: loadingAccounts } =
    useGetDDLAccountListQuery();

  const [fetchEntities, { data: entities, isLoading: loadingEntities }] =
    useLazyGetDDLEntityListQuery();

  const [
    fetchServiceBranches,
    {
      data: serviceBranches,
      isLoading: loadingServiceBranches,
      error: serviceBranchError,
    },
  ] = useLazyGetDDLBranchListQuery();

  const [
    fetchBranchesByProvince,
    { isLoading: loadingBranches, error: branchError },
  ] = useLazyGetBranchByRegionDDLQuery();

  const [
    fetchProvinces,
    { data: provincesData, isLoading: loadingProvinces, error: provinceError },
  ] = useLazyGetDDLRestrictedRegionListQuery();

  const loading =
    isBusy ||
    loadingVenUsers ||
    fetchingVenUsers ||
    loadingServiceBranches ||
    loadingProvinces;

  const _mappedUsers = React.useMemo(
    () => userVenList?.map((el) => ({ label: el?.name, id: el?._id })) || [],
    [userVenList]
  );

  const _mappedBranches = React.useMemo(() => {
    const dataArray = Array.isArray(serviceBranches)
      ? serviceBranches
      : Array.isArray(serviceBranches?.data)
        ? serviceBranches.data
        : [];
    return dataArray
      .filter((item) => item?._id && item?.name)
      .map((el) => ({
        label: `${el?.name || "Unknown"} - ${el?.code || "N/A"}`,
        id: el?._id,
      }));
  }, [serviceBranches]);

  const _mappedProvinces = React.useMemo(() => {
    const dataArray = Array.isArray(provincesData)
      ? provincesData
      : Array.isArray(provincesData?.data)
        ? provincesData.data
        : [];
    return dataArray
      .filter((item) => item?._id && item?.name)
      .map((item) => ({ label: item.name, id: item._id }));
  }, [provincesData]);

  React.useEffect(() => {
    const accountId =
      _watchAccount?._id ?? viewEntity?.entity?.account?._id?._id;
    const entityId = _watchEntity?._id ?? viewEntity?.entity?._id;

    fetchServiceBranches({ account: accountId, entity: entityId });
    fetchProvinces({ account: accountId, entity: entityId });
    fetchEntities({ account: accountId });

  }, [_watchAccount?._id, _watchEntity?._id, fetchEntities, fetchProvinces, fetchServiceBranches, isAccountAdmin, isSystemAdmin, viewEntity, _watchAccount, _watchEntity]);

  const selectedProvinces = watch("province");

  React.useEffect(() => {
    const accountId = _watchAccount?._id ?? viewEntity?.entity?.account?._id?._id;
    const entityId = _watchEntity?._id ?? viewEntity?.entity?._id;
    const provinceIds = (selectedProvinces || []).map((province) => province?.id).filter(Boolean);

    if (!accountId || !entityId || !provinceIds.length) {
      setValue("branch", []);
      return;
    }

    Promise.allSettled(
      provinceIds.map((regionId) =>
        fetchBranchesByProvince({ regionId, account: accountId, entity: entityId }).unwrap()
      )
    ).then((results) => {
      const branchOptions = results
        .filter((result) => result.status === "fulfilled")
        .flatMap((result) => result.value?.data || [])
        .filter((branch, index, all) => all.findIndex((item) => item._id === branch._id) === index)
        .map((branch) => ({
          label: `${branch.name || "Unknown"} - ${branch.code || "N/A"}`,
          id: branch._id,
          provinceId: branch.region?._id || branch.region,
        }));
      setBranchOptions(branchOptions);
      setValue("branch", (watch("branch") || []).filter((selectedBranch) =>
        branchOptions.some((option) => option.id === selectedBranch.id)
      ));
    });
  }, [selectedProvinces, _watchAccount, _watchEntity, viewEntity, fetchBranchesByProvince, setValue, watch]);

  React.useEffect(() => {
    fetchVenUsers();
  }, [fetchVenUsers, viewEntity]);

  React.useEffect(() => {
    if (serviceBranchError) {
      toast.error("Failed to fetch branches: " + (serviceBranchError?.data?.message || "Unknown error"));
    }
  }, [serviceBranchError]);

  React.useEffect(() => {
    if (provinceError) {
      toast.error(
        "Failed to fetch provinces: " +
          (provinceError?.data?.message || "Unknown error")
      );
    }
  }, [provinceError]);

  React.useEffect(() => {
    if (vendorProfileDetail) {
      reset({
        ...vendorProfileDetail,
        isActive: vendorProfileDetail.status === "Active",
        coordinates: vendorProfileDetail.coordinates || { lat: "", long: "" },
        branch: (Array.isArray(vendorProfileDetail.branch) ? vendorProfileDetail.branch : [vendorProfileDetail.branch]).filter(Boolean).map((branch) => ({ label: `${branch.name || "Unknown"} - ${branch.code || "N/A"}`, id: branch._id || branch.id || branch })),
        province: (Array.isArray(vendorProfileDetail.province) ? vendorProfileDetail.province : [vendorProfileDetail.province]).filter(Boolean).map((province) => ({ label: province.name || "Unknown", id: province._id || province.id || province })),
        relationshipManager: vendorProfileDetail.relationshipManager
          ? {
            label: vendorProfileDetail.relationshipManager.name,
            id: vendorProfileDetail.relationshipManager._id,
          }
          : null,
        referredBy: vendorProfileDetail.referredBy
          ? {
            label: vendorProfileDetail.referredBy.name,
            id: vendorProfileDetail.referredBy._id,
          }
          : null,
        onboardedBy: vendorProfileDetail.onboardedBy
          ? {
            label: vendorProfileDetail.onboardedBy.name,
            id: vendorProfileDetail.onboardedBy._id,
          }
          : null,
        serviceBranches:
          vendorProfileDetail.serviceBranches?.flatMap((sb) =>
            (sb?.branch || []).map((b) => ({
              label: `${b?.name || "Unknown"} - ${b?.code || "N/A"}`,
              id: b?._id || b,
            }))
          ) || [],
      });
    } else {
      reset({
        ...INITIAL_VALUE,
        account: viewEntity?.entity?.account || "",
        entity: viewEntity?.entity,
      });
    }
  }, [vendorProfileDetail, reset, viewEntity]);

  const handleAddressCreate = async (data) => {
    try {
      await createAddress(data).unwrap();
      toast.success("CAN address created successfully");
      setIsAddAddressModalActive(false);
    } catch (err) {
      toast.error(
        err?.data?.message ?? err?.message ?? "Couldn't create vendor Address!"
      );
    }
  };

  const handleVendorProfile = (data) => {
    onSubmit({
      ...data,
      referredBy: data?.referredBy?.id ?? null,
      relationshipManager: data?.relationshipManager?.id ?? null,
      onboardedBy: data?.onboardedBy?.id ?? null,
      branch: data?.branch?.map((item) => item.id) || [],
      province: data?.province?.map((item) => item.id) || [],
      account: data?.account?._id,
      entity: data?.entity?._id,
      isActive: !!data?.isActive,
      serviceBranches:
        data?.serviceBranches?.map((branch) => branch.id).filter(Boolean) || [],
    });
  };

  const formatSelectOptions = (options, isSimpleArray = false) => {
    if (isSimpleArray) return options || [];
    return (
      options?.map((option) => ({
        value: option.value || option._id,
        label: option.label || option.name || option.value || option._id,
      })) || []
    );
  };

  return (
    <>
      <AppForm loading={loading}>
        <AppForm.Content>
          <Grid container spacing={6}>
            {(isAccountAdmin || isSystemAdmin) && (
              <>
                <Grid item xs={12} sm={6}>
                  <Controller
                    name="account"
                    control={control}
                    rules={{ required: "Please select an account." }}
                    render={({ field: { value, onChange } }) => (
                      <BaseAutocomplete
                        fullWidth
                        label="Account"
                        required
                        value={value || ""}
                        onChange={(_, data) => {
                          onChange(data);
                          setValue("entity", "");
                          setValue("branch", []);
                          setValue("province", []);
                          setValue("serviceBranches", []);
                        }}
                        getOptionLabel={(option) => option?.name || ""}
                        isOptionEqualToValue={(option, value) =>
                          option?._id === value?._id
                        }
                        options={accounts || []}
                        loading={loadingAccounts}
                        loadingText="Loading Account..."
                        disabled={isAccountAdmin}
                        helperText={errors?.account?.message}
                        error={!!errors?.account}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Controller
                    name="entity"
                    control={control}
                    rules={{ required: "Please select an entity." }}
                    render={({ field: { value, onChange } }) => (
                      <BaseAutocomplete
                        fullWidth
                        label="Entity"
                        required
                        value={value || ""}
                        onChange={(_, data) => {
                          onChange(data);
                          setValue("branch", []);
                          setValue("province", []);
                          setValue("serviceBranches", []);
                        }}
                        getOptionLabel={(option) => option?.name || ""}
                        isOptionEqualToValue={(option, value) =>
                          option?._id === value?._id
                        }
                        options={entities || []}
                        loading={loadingEntities}
                        loadingText="Loading Entities..."
                        helperText={errors?.entity?.message}
                        error={!!errors?.entity}
                      />
                    )}
                  />
                </Grid>
              </>
            )}
            <Grid item md={6}>
              <Controller
                name="shopName"
                control={control}
                rules={{ required: "Shop Name is required" }}
                render={({ field }) => (
                  <BaseTextField
                    fullWidth
                    label="Shop Name / Trading Name"
                    type="text"
                    size="small"
                    required
                    {...field}
                  />
                )}
              />
              {errors.shopName && (
                <p className="error">{errors.shopName.message}</p>
              )}
            </Grid>
            <Grid item md={6}>
              <Controller
                name="phone"
                control={control}
                rules={{
                  required: "Phone is required",
                  validate: (value) => {
                    const digits = value.replace(/\D/g, "");
                    if (digits.length < 7 || digits.length > 17) {
                      return "Phone number must be between 9 and 16 digits";
                    }
                    return true;
                  },
                }}
                render={({ field, fieldState: { error } }) => (
                  <CustomCountryPhoneInput
                    placeholder="Phone Number"
                    label="Phone*"
                    {...field}
                    error={!!error}
                    helperText={error?.message}
                  />
                )}
              />
            </Grid>
            <Grid item md={6}>
              <Controller
                name="email"
                control={control}
                rules={{
                  required: "Email is required",
                  pattern: {
                    value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA]{2,}$/,
                    message: "Invalid email address",
                  },
                }}
                render={({ field }) => (
                  <BaseTextField
                    fullWidth
                    label="Email"
                    type="email"
                    size="small"
                    required
                    {...field}
                    error={!!errors.email}
                    helperText={errors?.email?.message}
                  />
                )}
              />
            </Grid>
            <Grid item md={6}>
              <Controller
                name="website"
                control={control}
                render={({ field }) => (
                  <BaseTextField
                    fullWidth
                    label="Website"
                    type="text"
                    size="small"
                    {...field}
                  />
                )}
              />
            </Grid>
            <Grid item xs={6}>
              <Controller
                name="address"
                control={control}
                rules={{ required: "Please provide address." }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    required
                    size="small"
                    fullWidth
                    label="Address"
                    placeholder="Enter Address"
                    error={!!errors?.address}
                    helperText={errors?.address?.message}
                  />
                )}
              />
            </Grid>
            <Grid item md={6}>
              <Controller
                name="ownerName"
                control={control}
                rules={{ required: "Owner Name is required" }}
                render={({ field }) => (
                  <BaseTextField
                    fullWidth
                    label="Owner Name"
                    type="text"
                    size="small"
                    {...field}
                  />
                )}
              />
              {errors.ownerName && (
                <p className="error">{errors.ownerName.message}</p>
              )}
            </Grid>
            <Grid item md={6} xs={12}>
              <Controller
                name="province"
                control={control}
                rules={{ required: "Province is required" }}
                render={({ field }) => (
                  <BaseAutocomplete
                    fullWidth
                    required
                    label="Province"
                    loading={loadingProvinces}
                    loadingText="Loading Provinces..."
                    options={_mappedProvinces || []}
                    multiple
                    value={field?.value || []}
                    onChange={(_, data) => {
                      field.onChange(data);
                      const provinceIds = data.map((province) => province.id);
                      setValue(
                        "branch",
                        (watch("branch") || []).filter((selectedBranch) => {
                          const provinceId =
                            selectedBranch.provinceId ||
                            branchOptions.find((option) => option.id === selectedBranch.id)?.provinceId;
                          return !provinceId || provinceIds.includes(provinceId);
                        })
                      );
                    }}
                    getOptionLabel={(opt) => opt?.label || ""}
                    isOptionEqualToValue={(option, value) =>
                      option?.id === value?.id
                    }
                    helperText={
                      errors?.province?.message ||
                      (provinceError ? "Failed to load provinces" : "")
                    }
                    error={!!errors?.province || !!provinceError}
                  />
                )}
              />
            </Grid>
            <Grid item md={6} xs={12}>
              <Controller
                name="branch"
                control={control}
                rules={{ required: "Branch is required" }}
                render={({ field }) => (
                  <BaseAutocomplete
                    fullWidth
                    required
                    label="Branch"
                    loading={loadingBranches}
                    loadingText="Loading branches..."
                    multiple
                    disabled={!selectedProvinces.length}
                    value={field?.value || []}
                    options={branchOptions}
                    onChange={(_, data) => field.onChange(data)}
                    getOptionLabel={(opt) => opt?.label || ""}
                    isOptionEqualToValue={(option, value) =>
                      option?.id === value?.id
                    }
                    helperText={
                      errors?.branch?.message ||
                      (branchError?.status !== 404 && branchError ? "Failed to load branches" : "")
                    }
                    error={!!errors?.branch || (branchError?.status !== 404 && !!branchError)}
                  />
                )}
              />
            </Grid>
            {!vendorProfileDetail && (
              <Grid item xs={6}>
                <Controller
                  name="serviceBranches"
                  control={control}
                  rules={{
                    required: "Please select at least one service branch.",
                  }}
                  render={({ field: { value, onChange } }) => (
                    <BaseAutocomplete
                      multiple
                      fullWidth
                      label="Service Branches"
                      required
                      value={value ?? []}
                      onChange={(_, data) => onChange(data)}
                      getOptionLabel={(option) => option?.label || ""}
                      isOptionEqualToValue={(option, value) =>
                        option?.id === value?.id
                      }
                      options={_mappedBranches || []}
                      loading={loadingServiceBranches}
                      loadingText="Loading branches..."
                      componentsProps={{
                        popper: {
                          placement: "bottom-start",
                          modifiers: [{ name: "flip", enabled: false }],
                        },
                      }}
                      helperText={
                        errors?.serviceBranches?.message ||
                        (serviceBranchError ? "Failed to load branches" : "")
                      }
                      error={!!errors?.serviceBranches || !!serviceBranchError}
                    />
                  )}
                />
              </Grid>
            )}
            {vendorProfileDetail && (isAccountAdmin || isSystemAdmin) && (
              <Grid item xs={6}>
                <Controller
                  name="paymentTerms"
                  control={control}
                  rules={{ required: "Payment Terms is required." }}
                  render={({ field }) => (
                    <BaseSelect
                      {...field}
                      required
                      fullWidth
                      size="small"
                      label="Payment Terms"
                      options={formatSelectOptions(PAYMENT_TERMS_OPTIONS, "label")}
                      helperText={errors?.paymentTerms?.message}
                      error={!!errors?.paymentTerms}
                    />
                  )}
                />
              </Grid>
            )}
            <Grid item md={6}>
              <Controller
                name="registrationNo"
                control={control}
                rules={{ required: "PAN/VAT Number is required" }}
                render={({ field }) => (
                  <BaseTextField
                    fullWidth
                    required
                    label="PAN/VAT Number"
                    type="text"
                    size="small"
                    {...field}
                  />
                )}
              />
              {errors.registrationNo && (
                <p className="error">{errors.registrationNo.message}</p>
              )}
            </Grid>
            <Grid item md={6}>
              <Controller
                name="relationshipManager"
                control={control}
                rules={{ required: "Please select a Relationship Manager." }}
                render={({ field }) => (
                  <BaseAutocomplete
                    fullWidth
                    required
                    label="Relationship Manager"
                    loading={loadingVenUsers || fetchingVenUsers}
                    loadingText="Loading Users..."
                    options={_mappedUsers || []}
                    value={field?.value || null}
                    onChange={(_, data) => field.onChange(data)}
                    getOptionLabel={(opt) => opt?.label || ""}
                    isOptionEqualToValue={(option, value) =>
                      option?.id === value?.id
                    }
                    helperText={errors?.relationshipManager?.message}
                    error={!!errors?.relationshipManager}
                  />
                )}
              />
            </Grid>
            <Grid item md={6}>
              <Controller
                name="referredBy"
                control={control}
                rules={{ required: "Please select Referred By." }}
                render={({ field }) => (
                  <BaseAutocomplete
                    fullWidth
                    required
                    label="Referred By"
                    loading={loadingVenUsers || fetchingVenUsers}
                    loadingText="Loading Users..."
                    options={_mappedUsers || []}
                    value={field?.value || null}
                    onChange={(_, data) => field.onChange(data)}
                    getOptionLabel={(opt) => opt?.label || ""}
                    isOptionEqualToValue={(option, value) =>
                      option?.id === value?.id
                    }
                    helperText={errors?.referredBy?.message}
                    error={!!errors?.referredBy}
                  />
                )}
              />
            </Grid>
            <Grid item md={6}>
              <Controller
                name="onboardedBy"
                control={control}
                rules={{ required: "Please select Onboarded By." }}
                render={({ field }) => (
                  <BaseAutocomplete
                    fullWidth
                    required
                    label="Onboarded By"
                    loading={loadingVenUsers || fetchingVenUsers}
                    loadingText="Loading Users..."
                    options={_mappedUsers || []}
                    value={field?.value || null}
                    onChange={(_, data) => field.onChange(data)}
                    getOptionLabel={(opt) => opt?.label || ""}
                    isOptionEqualToValue={(option, value) =>
                      option?.id === value?.id
                    }
                    helperText={errors?.onboardedBy?.message}
                    error={!!errors?.onboardedBy}
                  />
                )}
              />
            </Grid>
            {!isVendor && vendorProfileDetail && (
              <Grid item md={6}>
                <Controller
                  name="isActive"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={
                        <Switch
                          name="isActive"
                          color="primary"
                          checked={field.value}
                          {...field}
                        />
                      }
                      label="Active"
                    />
                  )}
                />
                {errors.status && (
                  <p className="error">{errors.status.message}</p>
                )}
              </Grid>
            )}
          </Grid>
        </AppForm.Content>
        <AppForm.Actions
          paddingX={3}
          paddingY={2}
          gap={2}
          style={{
            display: "flex",
            justifyContent: "flex-end",
          }}
        >
          <Button disabled={isBusy} onClick={() => navigate(-1)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={isBusy}
            onClick={handleSubmit(handleVendorProfile)}
          >
            {vendorProfileDetail ? "Update" : "Add"}
          </Button>
        </AppForm.Actions>
      </AppForm>
      <BaseModal
        isOpen={isAddAddressModalActive}
        onClose={() => {
          setIsAddAddressModalActive(false);
        }}
        title="Add CAN Address"
        width="1200px"
        top="50%"
        left="50%"
      >
        <NewAddressForm
          address={address}
          canAddress={canAddress}
          label="Address"
          isBusy={creatingAddress}
          onSubmit={handleAddressCreate}
          onCancel={() => {
            setIsAddAddressModalActive(false);
          }}
        />
      </BaseModal>
    </>
  );
};

export default VendorProfileForm;
